"use client";

import { createContext, useContext, useEffect, useState } from "react";
import { useToast } from "@/hooks/use-toast";

type LanguageContextType = {
    language: string;
    setLanguage: (lang: string) => Promise<void>;
    t: (key: string) => string;
};

// Recursive type to match any JSON structure
type JsonValue = string | number | boolean | null | JsonObject | JsonArray;
type JsonObject = { [key: string]: JsonValue };
type JsonArray = JsonValue[];

const LanguageContext = createContext<LanguageContextType | undefined>(
    undefined
);

export function RequestLanguageProvider({
    children,
    initialLanguage,
}: {
    children: React.ReactNode;
    initialLanguage: string;
}) {
    const [language, setLanguageState] = useState(initialLanguage);
    const [messages, setMessages] = useState<JsonObject | null>(null);
    const { toast } = useToast();

    useEffect(() => {
        const loadMessages = async () => {
            const msgs = await import(`@/messages/${language}.json`);
            setMessages(msgs.default);
        };
        loadMessages();
    }, [language]);

    const setLanguage = async (newLang: string) => {
        try {
            // Store the language preference in a cookie
            const response = await fetch("/api/language", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ language: newLang }),
            });

            if (!response.ok) {
                throw new Error("Failed to update language");
            }

            setLanguageState(newLang);
            const successMessage =
                messages?.errors &&
                typeof messages.errors === "object" &&
                "languageUpdated" in messages.errors
                    ? ((messages.errors as JsonObject)
                          .languageUpdated as string)
                    : "Language updated successfully";

            toast({
                title: successMessage,
            });
        } catch (error) {
            console.error("Failed to update language:", error);
            const errorMessage =
                messages?.errors &&
                typeof messages.errors === "object" &&
                "languageUpdateFailed" in messages.errors
                    ? ((messages.errors as JsonObject)
                          .languageUpdateFailed as string)
                    : "Failed to update language";

            toast({
                title: errorMessage,
                variant: "destructive",
            });
        }
    };

    const t = (key: string) => {
        if (!messages) return key;
        const keys = key.split(".");
        let value: JsonValue = messages;

        for (const k of keys) {
            if (typeof value === "object" && value !== null) {
                value = (value as JsonObject)[k];
            } else {
                return key;
            }
            if (value === undefined || value === null) return key;
        }

        return typeof value === "string" ? value : key;
    };

    return (
        <LanguageContext.Provider value={{ language, setLanguage, t }}>
            {children}
        </LanguageContext.Provider>
    );
}
export function useRequestLanguage(shouldThrow?: true): LanguageContextType;
export function useRequestLanguage(
    shouldThrow: false
): LanguageContextType | undefined;
export function useRequestLanguage(
    shouldThrow = true
): LanguageContextType | undefined {
    const context = useContext(LanguageContext);
    if (shouldThrow && !context) {
        throw new Error(
            "useRequestLanguage must be used within a RequestLanguageProvider"
        );
    }
    return context;
}
